import React, { useState } from "react";
import { Box, Typography } from "@mui/joy";
import type { SxProps } from "@mui/joy/styles/types";
import { AlertCircle, AlertTriangle, CheckCircle } from "lucide-react";

import Button from "../Button";
import { useApiResponseParser } from "../../hooks";

type AlertTone = "success" | "danger" | "warning";

interface IAlertProps {
    successMessage?: string | React.ReactElement | null | false;
    errorMessage?: any;
    warningMessage?: string | React.ReactElement | null | false;
    /** Optional override for the status title (defaults by tone). */
    title?: string;
    sx?: SxProps;
    [key: string]: any;
}

const TONE_CONFIG: Record<
    AlertTone,
    {
        defaultTitle: string;
        color: AlertTone;
        Icon: typeof CheckCircle;
    }
> = {
    success: {
        defaultTitle: "Success",
        color: "success",
        Icon: CheckCircle,
    },
    danger: {
        defaultTitle: "Something went wrong",
        color: "danger",
        Icon: AlertCircle,
    },
    warning: {
        defaultTitle: "Warning",
        color: "warning",
        Icon: AlertTriangle,
    },
};

function toMessageContent(
    message: string | React.ReactElement | null | false | undefined,
): React.ReactNode {
    if (message == null || message === false) {
        return null;
    }
    if (React.isValidElement(message)) {
        return message;
    }
    return String(message);
}

export default function Alert(props: IAlertProps) {
    const {
        successMessage,
        errorMessage,
        warningMessage,
        title,
        sx,
        ...otherProps
    } = props;
    const { parseErrorResponseData } = useApiResponseParser();
    const [reported, setReported] = useState(false);

    const errorDisplayMessage = parseErrorResponseData(errorMessage);
    const requestId =
        errorMessage?.response?.data?.MetaData?.RequestId ||
        errorMessage?.MetaData?.RequestId ||
        null;

    let tone: AlertTone | null = null;
    let body: React.ReactNode = null;

    if (errorDisplayMessage) {
        tone = "danger";
        body = errorDisplayMessage;
    } else if (warningMessage) {
        tone = "warning";
        body = toMessageContent(warningMessage);
    } else if (successMessage) {
        tone = "success";
        body = toMessageContent(successMessage);
    }

    if (!tone || body == null || body === "") {
        return null;
    }

    const { defaultTitle, color, Icon } = TONE_CONFIG[tone];
    const heading = title || defaultTitle;

    const handleReport = () => {
        const raw = errorMessage?.response?.data || {};
        const reportData = {
            ErrorName: raw.Error?.Name || "BackendError",
            ErrorMessage:
                typeof errorDisplayMessage === "string"
                    ? errorDisplayMessage
                    : "Unknown Error",
            RequestId: requestId || raw.MetaData?.RequestId,
            InitTimestamp: raw.MetaData?.InitTimestamp,
            CompleteTimestamp: raw.MetaData?.CompleteTimestamp,
            StackTrace: raw.Error?.Stack,
        };

        console.log("---------- ERROR REPORT ----------");
        console.table(reportData);
        console.log("Full Raw Data:", raw);
        console.log("----------------------------------");
        setReported(true);
    };

    return (
        <Box
            role="alert"
            {...otherProps}
            sx={[
                {
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "stretch", sm: "flex-start" },
                    gap: 1.5,
                    px: 2,
                    py: 1.75,
                    width: "100%",
                    boxSizing: "border-box",
                    borderRadius: "4px",
                    border: "1px solid",
                    borderColor: `${color}.outlinedBorder`,
                    bgcolor: `${color}.softBg`,
                },
                ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
            ]}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.25,
                    flex: 1,
                    minWidth: 0,
                }}
            >
                <Box
                    sx={{
                        color: "#fff",
                        bgcolor: `${color}.solidBg`,
                        borderRadius: "50%",
                        width: 32,
                        height: 32,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        mt: 0.15,
                    }}
                >
                    <Icon size={18} />
                </Box>

                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography level="title-sm" color={color}>
                        {heading}
                    </Typography>
                    <Typography
                        level="body-xs"
                        sx={{
                            color: "text.secondary",
                            mt: 0.25,
                            wordBreak: "break-word",
                        }}
                        component="div"
                    >
                        {body}
                    </Typography>
                    {tone === "danger" && requestId ? (
                        <Typography
                            level="body-xs"
                            sx={{
                                mt: 0.75,
                                color: "text.tertiary",
                                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                                fontSize: "11px",
                                letterSpacing: "0.02em",
                                wordBreak: "break-all",
                            }}
                        >
                            Request ID: {requestId}
                        </Typography>
                    ) : null}
                </Box>
            </Box>

            {tone === "danger" ? (
                <Button
                    size="sm"
                    variant={reported ? "soft" : "solid"}
                    color="danger"
                    onClick={handleReport}
                    disabled={reported}
                    sx={{
                        borderRadius: "4px",
                        flexShrink: 0,
                        alignSelf: { xs: "stretch", sm: "center" },
                    }}
                >
                    {reported ? "Logged" : "Report"}
                </Button>
            ) : null}
        </Box>
    );
}
